/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ISignUpEmailVerification,
    ISignUpGivenNameVerification,
    ISignUpItem,
    ISignUpLoading,
    ISignUpLocalAccount,
    ISignUpSurnameVerification,
    ISignUpViewProps
} from './dobbies-sign-up';
import PasswordStates from './components/doobies-sign-up-password-states';

const SignUpItem: React.FC<ISignUpItem> = ({ wrapper, label, errorMessage, input }) => {
    return (
        <Node {...wrapper}>
            {label}
            {errorMessage}
            {input}
        </Node>
    );
};

const SignUpEmailVerification: React.FC<ISignUpEmailVerification> = ({ email, buttonWrapper, buttons, successMessage, errorMessage }) => {
    return (
        <>
            {successMessage &&
                successMessage.map((message: React.ReactNode) => {
                    return <>{message}</>;
                })}
            <SignUpItem {...email} key={email.key} />
            {errorMessage &&
                errorMessage.map((message: React.ReactNode) => {
                    return <>{message}</>;
                })}
            <Node {...buttonWrapper}>
                {buttons &&
                    buttons.map((button: React.ReactNode) => {
                        return <>{button}</>;
                    })}
            </Node>
        </>
    );
};

/**
 * Handles validation of sign-up givenName.
 * @param event - Content editable event.
 * @param event.givenName - Event Given Name.
 * @param event.errorMessage - Event Error Message.
 * @returns - Error Message for Given Name.
 */
const SignUpGivenNameVerificationComponent: React.FC<ISignUpGivenNameVerification> = ({ givenName, errorMessage }) => {
    return (
        <>
            <SignUpItem {...givenName} />
            {errorMessage.map((message: React.ReactNode) => {
                return <>{message}</>;
            })}
        </>
    );
};

/**
 * Handles validation of signUp surname.
 * @param event - Content editable event.
 * @param event.surname - Event surname.
 * @param event.errorMessage - Event Error Message.
 * @returns - Error Message for surname.
 */
const SignUpSurnameVerificationComponent: React.FC<ISignUpSurnameVerification> = ({ surname, errorMessage }) => {
    return (
        <>
            <SignUpItem {...surname} />
            {errorMessage.map((message: React.ReactNode) => {
                return <>{message}</>;
            })}
        </>
    );
};

const LocalAccount: React.FC<ISignUpLocalAccount> = (
    { localAccount, items, emailVerification, givenNameVerification, surnameVerification, buttons, errorMessage, disclaimer, passItems },
    active
) => {
    return (
        <Node {...localAccount} key=''>
            <div className={active ? 'inactive' : 'active'}>
                {items &&
                    items.map((item: ISignUpItem) => {
                        if (item.key === 'email' && emailVerification.isRequired) {
                            return (
                                <Node {...emailVerification.verificationControlWrapper} key='email'>
                                    <SignUpItem {...item} key={item.key} />
                                    <SignUpEmailVerification {...emailVerification} />
                                </Node>
                            );
                        }
                        if (item.key === 'givenName' && givenNameVerification.isRequired) {
                            return (
                                <Node {...givenNameVerification.verificationControlWrapper} key='givenName'>
                                    <SignUpItem {...item} />
                                    <SignUpGivenNameVerificationComponent {...givenNameVerification} />
                                </Node>
                            );
                        }
                        if (item.key === 'surname' && surnameVerification.isRequired) {
                            return (
                                <Node {...surnameVerification.verificationControlWrapper} key='surname'>
                                    <SignUpItem {...item} />
                                    <SignUpSurnameVerificationComponent {...surnameVerification} />
                                </Node>
                            );
                        }
                        return <SignUpItem {...item} key={item.key} />;
                    })}
            </div>
            <div className={active ? 'active password-input' : 'inactive'}>
                {' '}
                {passItems &&
                    passItems.map(item => {
                        return <SignUpItem {...item} key={item.key} />;
                    })}
                <li id='CheckboxMultiSelect' className='CheckboxMultiSelect'>
                    <div className='attrEntry'>
                        <label id='mktOptions_label' htmlFor='mktOptions'>
                            Please let us know how you would like to hear from us:
                        </label>
                        <div className='error itemLevel' role='alert' />
                        <input
                            id='mktOptions_Email'
                            name='mktOptions'
                            type='checkbox'
                            aria-labelledby='mktOptions_label mktOptions_Email_option'
                            defaultChecked={true}
                            defaultValue='Contactable Email'
                            value='Contactable Email'
                        />
                        <label id='Email_option' htmlFor='mktOptions_Email'>
                            Email
                        </label>
                        <input
                            id='mktOptions_Post'
                            name='mktOptions'
                            type='checkbox'
                            aria-labelledby='mktOptions_label mktOptions_Post_option'
                            defaultValue='Contactable Post'
                            value='Contactable Post'
                        />
                        <label id='Post_option' htmlFor='mktOptions_Post'>
                            Post{' '}
                        </label>
                        <input
                            id='mktOptions_SMS'
                            name='mktOptions'
                            type='checkbox'
                            aria-labelledby='mktOptions_label mktOptions_SMS_option'
                            defaultValue='Contactable Mobile'
                            value='Contactable Mobile'
                        />
                        <label id='SMS_option' htmlFor='mktOptions_SMS'>
                            SMS
                        </label>
                    </div>
                </li>
                {errorMessage &&
                    errorMessage.map((error: React.ReactNode, index: number) => {
                        return <React.Fragment key={index}>{error}</React.Fragment>;
                    })}
                {buttons &&
                    buttons.map((button: React.ReactNode, index: number) => {
                        return <React.Fragment key={index}>{button}</React.Fragment>;
                    })}
                {disclaimer}
            </div>
        </Node>
    );
};

const SignUpLoading: React.FC<ISignUpLoading> = ({ modal, modalBody, icon, message }) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const SignUpView: React.FC<ISignUpViewProps> = props => {
    const { signUp, viewState, loading, defaultAADConainer, aadConainer, heading, signUpLocalAccount } = props;
    const [active, setActive] = React.useState(false);

    return (
        <Module {...signUp}>
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignUpLoading {...loading} />}
            <div className='step-breadcrums'>
                <a
                    className={active ? 'inactive' : 'active'}
                    style={{ cursor: 'pointer' }}
                    onClick={e => {
                        console.log('clicked');
                        active === false ? setActive(true) : setActive(false);
                    }}
                >
                    Details
                </a>
                <a
                    className={active ? 'active' : 'inactive inactive-pass'}
                    style={{ cursor: 'pointer' }}
                    onClick={e => {
                        console.log('clicked');
                        active === false ? setActive(true) : setActive(false);
                    }}
                >
                    Password
                </a>
            </div>
            <div className={active ? 'step-one-details active-step' : 'inactive-step step-one-details'}>
                <p>
                    <label>First name*</label> <b id='firstName'></b>
                </p>
                <p>
                    <label>Last name*</label> <b id='lastName'></b>
                </p>
                <p>
                    <label>Email*</label> <b id='verifiedEmail'></b>
                </p>
            </div>
            <PasswordStates />
            <Node {...aadConainer}>
                {heading}
                {LocalAccount(signUpLocalAccount, active)}
            </Node>
        </Module>
    );
};

export default SignUpView;
